/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Calendar,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  Clock,
  Info,
  LayoutList,
  Pencil,
  Sparkles,
  X,
} from "lucide-react";
import { amenitiesApi } from "./api";
import { useAuth } from "../auth/AuthContext";
import { toast } from "../ui";
import "./AmenitiesPage.css";

const OPERATOR_ROLES = new Set(["CLUBHOUSE_SECURITY", "SECURITY_INCHARGE", "ADMIN"]);
const ADMIN_ROLES = new Set(["ADMIN"]);

function toDateKey(d) {
  return d.toISOString().slice(0, 10);
}

function fmtSlot(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (_) { return iso; }
}

function fmtWindow(start, end) {
  if (!start) return "";
  try {
    const s = new Date(start);
    const e = end ? new Date(end) : null;
    const day = s.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
    const ts = s.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const te = e ? e.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
    return `${day}, ${ts}${te ? `–${te}` : ""}`;
  } catch (_) { return start; }
}

function statusLabel(s) {
  return { BOOKED: "Booked", VERIFIED: "Verified", CANCELLED: "Cancelled", EXPIRED: "Expired", NO_SHOW: "No-show" }[s] || s;
}

function statusClass(s) {
  return { BOOKED: "am-pill--blue", VERIFIED: "am-pill--green", CANCELLED: "am-pill--muted", EXPIRED: "am-pill--muted", NO_SHOW: "am-pill--muted" }[s] || "";
}

const AMENITY_ICONS = ["🏊", "🎾", "🏋️", "🛝", "🎯", "🎱", "🎉", "🏸"];

export default function AmenitiesPage() {
  const { user } = useAuth();
  const activeRole = user?.active_role || (user?.is_admin ? "ADMIN" : "RESIDENT");
  const isOperator = OPERATOR_ROLES.has(activeRole);
  const isAdmin = ADMIN_ROLES.has(activeRole);
  const [editingAmenity, setEditingAmenity] = useState(null);

  const [amenities, setAmenities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loadingA, setLoadingA] = useState(true);
  const [selected, setSelected] = useState(null);      // Amenity being booked
  const [selectedDate, setSelectedDate] = useState(toDateKey(new Date()));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [qrBooking, setQrBooking] = useState(null);    // Show QR sheet
  const [cancelling, setCancelling] = useState(null);  // booking id

  const loadAmenities = useCallback(async () => {
    try {
      const data = await amenitiesApi.list();
      setAmenities(Array.isArray(data) ? data : data.amenities || []);
    } catch (e) {
      toast.error("Could not load amenities");
    } finally {
      setLoadingA(false);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const data = await amenitiesApi.myBookings();
      const list = Array.isArray(data) ? data : data.bookings || [];
      setBookings(list);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadAmenities();
    loadBookings();
  }, [loadAmenities, loadBookings]);

  // Load slots when amenity or date changes
  useEffect(() => {
    if (!selected) return;
    setLoadingSlots(true);
    setSlots([]);
    amenitiesApi.slots(selected.id, selectedDate)
      .then((data) => setSlots(data.slots || []))
      .catch(() => toast.error("Could not load slots"))
      .finally(() => setLoadingSlots(false));
  }, [selected, selectedDate]);

  const bookSlot = async (slot) => {
    if (!slot.available || booking) return;
    setBooking(true);
    try {
      const b = await amenitiesApi.book({
        amenity_id: selected.id,
        slot_start: slot.slot_start,
      });
      toast.success("Booked! Show the code at the clubhouse.");
      setQrBooking(b);
      setSelected(null);
      await loadBookings();
    } catch (e) {
      const msg = e?.detail || e?.message || "Could not book that slot";
      toast.error(msg);
    } finally {
      setBooking(false);
    }
  };

  const cancelBooking = async (b) => {
    setCancelling(b.id);
    try {
      await amenitiesApi.cancel(b.id);
      toast.success("Booking cancelled");
      await loadBookings();
    } catch (e) {
      toast.error(e?.message || "Could not cancel");
    } finally {
      setCancelling(null);
    }
  };

  const now = Date.now();
  const upcoming = bookings.filter(
    (b) => (b.status === "BOOKED" || b.status === "VERIFIED") && new Date(b.slot_end).getTime() > now
  );
  const past = bookings.filter((b) => !upcoming.includes(b));

  // Date strip: today + next 13 days
  const dateStrip = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });
  const maxDate = selected
    ? toDateKey((() => { const d = new Date(); d.setDate(d.getDate() + (selected.max_advance_days || 30)); return d; })())
    : undefined;

  // ── Booking flow ─────────────────────────────────────────────────────
  if (selected) {
    const free = slots.filter((s) => s.available).length;
    return (
      <div className="am-page">
        <button className="am-back" onClick={() => setSelected(null)}>
          <ChevronLeft size={16} /> All amenities
        </button>
        <div className="am-hero">
          <span className="am-hero-icon">{AMENITY_ICONS[amenities.indexOf(selected) % AMENITY_ICONS.length]}</span>
          <div>
            <h2 className="am-hero-title">{selected.name}</h2>
            {selected.description && <p className="am-hero-desc">{selected.description}</p>}
          </div>
        </div>

        {/* Date strip */}
        <div className="am-section-label">Select date</div>
        <div className="am-date-strip">
          {dateStrip.map((d) => {
            const key = toDateKey(d);
            const active = key === selectedDate;
            const dow = d.toLocaleDateString([], { weekday: "short" });
            const day = d.getDate();
            return (
              <button
                key={key}
                className={`am-date-chip${active ? " active" : ""}`}
                onClick={() => setSelectedDate(key)}
                disabled={maxDate && key > maxDate}
              >
                <span className="am-date-dow">{dow}</span>
                <span className="am-date-day">{day}</span>
              </button>
            );
          })}
          <label className="am-date-chip am-date-pick" title="Pick another date">
            <CalendarDays size={18} />
            <input
              type="date"
              style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
              value={selectedDate}
              min={toDateKey(new Date())}
              max={maxDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            />
          </label>
        </div>

        {/* Slot grid */}
        <div className="am-section-label am-slots-label">
          Available slots
          {!loadingSlots && <span className={`am-free-count${free === 0 ? " empty" : ""}`}>{free === 0 ? "Full" : `${free} free`}</span>}
        </div>
        <div className="am-card">
          {loadingSlots ? (
            <div className="am-loading">Loading slots…</div>
          ) : slots.length === 0 ? (
            <div className="am-empty">No slots on this day. Try another date.</div>
          ) : (
            <div className="am-slot-grid">
              {slots.map((s) => (
                <button
                  key={s.slot_start}
                  className={`am-slot${s.available ? " available" : " taken"}`}
                  disabled={!s.available || booking}
                  onClick={() => bookSlot(s)}
                >
                  {fmtSlot(s.slot_start)}–{fmtSlot(s.slot_end)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Booking rules */}
        <div className="am-card am-rules">
          <div className="am-rules-head"><Info size={14} /> Booking rules</div>
          <ul className="am-rules-list">
            <li>Open {selected.open_from}–{selected.open_to}</li>
            <li>Book up to {selected.max_advance_days} day{selected.max_advance_days === 1 ? "" : "s"} ahead</li>
            <li>{selected.slot_minutes}-minute slots</li>
            <li>Up to {selected.max_active_per_villa} active booking{selected.max_active_per_villa === 1 ? "" : "s"} per villa</li>
          </ul>
        </div>
      </div>
    );
  }

  // ── Amenity list + my bookings ────────────────────────────────────────
  return (
    <div className="am-page">
      <div className="am-top">
        <h1 className="am-heading"><Sparkles size={20} /> Clubhouse & Amenities</h1>
        <p className="am-sub">
          {isOperator
            ? "Today's clubhouse bookings across the community."
            : "Book clubhouse facilities. Show the code or QR at the venue."}
        </p>
      </div>

      {isOperator && (
        <OperatorTodayPanel bookings={bookings} />
      )}

      {!isOperator && upcoming.length > 0 && (
        <>
          <div className="am-section-label"><CheckCircle2 size={14} /> My upcoming bookings</div>
          <div className="am-booking-list">
            {upcoming.map((b) => (
              <div key={b.id} className="am-booking-card">
                <div className="am-booking-main">
                  <strong>{b.amenity_name}</strong>
                  <span className="am-booking-time">{fmtWindow(b.slot_start, b.slot_end)}</span>
                </div>
                <div className="am-booking-right">
                  <span className={`am-pill ${statusClass(b.status)}`}>{statusLabel(b.status)}</span>
                  <button className="am-qr-btn" onClick={() => setQrBooking(b)}>Show QR</button>
                  {b.status === "BOOKED" && new Date(b.slot_start).getTime() > Date.now() && (
                    <button
                      className="am-cancel-btn"
                      disabled={cancelling === b.id}
                      onClick={() => cancelBooking(b)}
                    >
                      {cancelling === b.id ? "…" : "Cancel"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!isOperator && (
        <>
          <div className="am-section-label"><Sparkles size={14} /> Book an amenity</div>
          {loadingA ? (
            <div className="am-loading">Loading…</div>
          ) : amenities.length === 0 ? (
            <div className="am-empty am-empty--full">No bookable amenities yet.</div>
          ) : (
            <div className="am-grid">
              {amenities.map((a, i) => (
                <div key={a.id} className="am-tile-wrap">
                  <button className="am-tile" onClick={() => { setSelectedDate(toDateKey(new Date())); setSelected(a); }}>
                    <span className="am-tile-icon">{AMENITY_ICONS[i % AMENITY_ICONS.length]}</span>
                    <div className="am-tile-body">
                      <strong>{a.name}</strong>
                      <span className="am-tile-sub">{a.description || `${a.slot_minutes}-min slots · ${a.open_from}–${a.open_to}`}</span>
                    </div>
                    <Clock size={16} className="am-tile-arrow" />
                  </button>
                  {isAdmin && (
                    <button className="am-edit-btn" title="Edit config" onClick={() => setEditingAmenity(a)}>
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {past.length > 0 && (
            <>
              <div className="am-section-label am-past-label"><Clock size={14} /> Past bookings</div>
              <div className="am-past-list">
                {past.slice(0, 10).map((b) => (
                  <div key={b.id} className="am-past-row">
                    <div>
                      <strong>{b.amenity_name}</strong>
                      <span className="am-past-time">{fmtWindow(b.slot_start, b.slot_end)}</span>
                    </div>
                    <span className={`am-pill ${statusClass(b.status)}`}>{statusLabel(b.status)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Admin config modal */}
      {isAdmin && editingAmenity && (
        <AdminConfigModal
          amenity={editingAmenity}
          onClose={() => setEditingAmenity(null)}
          onSaved={(updated) => {
            setAmenities((cur) => cur.map((a) => a.id === updated.id ? updated : a));
            setEditingAmenity(null);
          }}
        />
      )}

      {/* QR/code modal */}
      {qrBooking && (
        <div className="am-modal-overlay" onClick={() => setQrBooking(null)}>
          <div className="am-modal" onClick={(e) => e.stopPropagation()}>
            <button className="am-modal-close" onClick={() => setQrBooking(null)}><X size={18} /></button>
            <h3>{qrBooking.amenity_name}</h3>
            <p className="am-modal-time">{fmtWindow(qrBooking.slot_start, qrBooking.slot_end)}</p>
            <div className="am-modal-code">{qrBooking.short_code}</div>
            <p className="am-modal-hint">Show this code or QR to the venue staff</p>
            {qrBooking.short_code && (
              <div className="am-qr-wrap">
                <QRCodeSVG value={qrBooking.short_code} size={160} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OperatorTodayPanel({ bookings }) {
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const active = bookings.filter((b) => b.status === "BOOKED" || b.status === "VERIFIED");
  return (
    <div className="am-operator-panel">
      <div className="am-section-label"><LayoutList size={14} /> Today's bookings — {today}</div>
      {bookings.length === 0 ? (
        <div className="am-empty am-empty--full">No bookings today.</div>
      ) : (
        <div className="am-op-table">
          <div className="am-op-row am-op-header">
            <span>Resident</span>
            <span>Villa</span>
            <span>Amenity</span>
            <span>Slot</span>
            <span>Status</span>
          </div>
          {bookings.map((b) => (
            <div key={b.id} className="am-op-row">
              <span className="am-op-name">{b.resident_name || "—"}</span>
              <span>{b.villa_number ? `${b.villa_number}` : "—"}</span>
              <span>{b.amenity_name}</span>
              <span className="am-op-slot">{fmtSlot(b.slot_start)}–{fmtSlot(b.slot_end)}</span>
              <span><span className={`am-pill ${statusClass(b.status)}`}>{statusLabel(b.status)}</span></span>
            </div>
          ))}
        </div>
      )}
      <p className="am-op-foot">{active.length} active · {bookings.length} total today</p>
    </div>
  );
}

function AdminConfigModal({ amenity, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: amenity.name || "",
    description: amenity.description || "",
    open_from: amenity.open_from || "06:00",
    open_to: amenity.open_to || "22:00",
    slot_minutes: amenity.slot_minutes ?? 60,
    max_advance_days: amenity.max_advance_days ?? 7,
    max_active_per_villa: amenity.max_active_per_villa ?? 1,
    is_active: amenity.is_active !== false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const field = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const updated = await amenitiesApi.patch(amenity.id, {
        ...form,
        slot_minutes: Number(form.slot_minutes),
        max_advance_days: Number(form.max_advance_days),
        max_active_per_villa: Number(form.max_active_per_villa),
      });
      onSaved(updated);
      toast.success("Amenity config saved");
    } catch (e) {
      setErr(e?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="am-modal-overlay" onClick={onClose}>
      <div className="am-modal am-config-modal" onClick={(e) => e.stopPropagation()}>
        <button className="am-modal-close" onClick={onClose}><X size={18} /></button>
        <h3>Edit amenity</h3>
        <form onSubmit={save} className="am-config-form">
          <label>
            Name
            <input value={form.name} onChange={(e) => field("name")(e.target.value)} required />
          </label>
          <label>
            Description
            <input value={form.description} onChange={(e) => field("description")(e.target.value)} />
          </label>
          <div className="am-config-row">
            <label>
              Opens
              <input type="time" value={form.open_from} onChange={(e) => field("open_from")(e.target.value)} required />
            </label>
            <label>
              Closes
              <input type="time" value={form.open_to} onChange={(e) => field("open_to")(e.target.value)} required />
            </label>
          </div>
          <div className="am-config-row">
            <label>
              Slot (min)
              <input type="number" min={15} step={15} value={form.slot_minutes} onChange={(e) => field("slot_minutes")(e.target.value)} required />
            </label>
            <label>
              Advance days
              <input type="number" min={1} max={90} value={form.max_advance_days} onChange={(e) => field("max_advance_days")(e.target.value)} required />
            </label>
            <label>
              Max per villa
              <input type="number" min={1} max={10} value={form.max_active_per_villa} onChange={(e) => field("max_active_per_villa")(e.target.value)} required />
            </label>
          </div>
          <label className="am-config-check">
            <input type="checkbox" checked={form.is_active} onChange={(e) => field("is_active")(e.target.checked)} />
            <span>Active (visible to residents)</span>
          </label>
          {err && <div className="am-config-err">{err}</div>}
          <div className="am-config-actions">
            <button type="button" className="am-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="am-btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
